import React, { useState } from 'react';

type StudentType = {
  name: string;
  achievement: string;
  institution: string;
};

type YearDataType = {
  totalStudents: number;
  medicalAdmissions: number;
  engineeringAdmissions: number;
  scienceDegrees: number;
  topScorers: number;
  highlights: string[];
  featuredStudents: StudentType[];
};

type SuccessDataType = {
  [key: string]: YearDataType;
};

const Success: React.FC = () => {
  const [activeYear, setActiveYear] = useState('2024');
  
  // Sample success data by year
  const successData: SuccessDataType = {
    '2024': {
      totalStudents: 120,
      medicalAdmissions: 45,
      engineeringAdmissions: 38,
      scienceDegrees: 25,
      topScorers: 12,
      highlights: [
        "45 students achieved top grades in O level Chemistry exams",
        "38 students achieved top grades in A level Chemistry exams",
        "98% have got distinction in O & A level Chemistry exams",
        "3 students achieved perfect scores in Chemistry"
      ],
      featuredStudents: [
        { name: "Ashfaq Rahman", achievement: "O level Topper", institution: "Cambridge Curriculum" },
        { name: "Nusrat Jahan", achievement: "A level Topper", institution: "Cambridge Curriculum" },
        { name: "Fahim Ahmed", achievement: "Got Scholarship", institution: "Cambridge Curriculum" }
      ]
    },
    '2023': {
      totalStudents: 105,
      medicalAdmissions: 40,
      engineeringAdmissions: 35,
      scienceDegrees: 20,
      topScorers: 10,
      highlights: [
        "40 students achieved top grades in O level Chemistry exams",
        "35 students achieved top grades in A level Chemistry exams",
        "96% have got distinction in O & A level Chemistry exams",
        "2 students achieved perfect scores in Chemistry"
      ],
      featuredStudents: [
        { name: "Tahmid Hasan", achievement: "O level Topper", institution: "Cambridge Curriculum" },
        { name: "Sabrina Khan", achievement: "A level Topper", institution: "Cambridge Curriculum" },
        { name: "Rafid Islam", achievement: "Got Scholarship", institution: "Cambridge Curriculum" }
      ]
    },
    '2022': {
      totalStudents: 95,
      medicalAdmissions: 35,
      engineeringAdmissions: 30,
      scienceDegrees: 18,
      topScorers: 8,
      highlights: [
        "35 students achieved top grades in O level Chemistry exams",
        "30 students achieved top grades in A level Chemistry exams",
        "95% have got distinction in O & A level Chemistry exams",
        "2 students achieved perfect scores in Chemistry"
      ],
      featuredStudents: [
        { name: "Raisa Ahmed", achievement: "O level Topper", institution: "Cambridge Curriculum" },
        { name: "Imran Hossain", achievement: "A level Topper", institution: "Cambridge Curriculum" },
        { name: "Tasnim Akter", achievement: "Got Scholarship", institution: "Cambridge Curriculum" }
      ]
    },
    '2021': {
      totalStudents: 90,
      medicalAdmissions: 32,
      engineeringAdmissions: 28,
      scienceDegrees: 15,
      topScorers: 7,
      highlights: [
        "32 students achieved top grades in O level Chemistry exams",
        "28 students achieved top grades in A level Chemistry exams",
        "94% have got distinction in O & A level Chemistry exams",
        "1 student achieved perfect score in Chemistry"
      ],
      featuredStudents: [
        { name: "Farhan Rahman", achievement: "O level Topper", institution: "Cambridge Curriculum" },
        { name: "Nabila Islam", achievement: "A level Topper", institution: "Cambridge Curriculum" },
        { name: "Sakib Khan", achievement: "Got Scholarship", institution: "Cambridge Curriculum" }
      ]
    }
  };
  
  const years = Object.keys(successData).sort((a, b) => parseInt(b) - parseInt(a));
  const currentYearData = successData[activeYear];

  return (
    <section id="success" className="py-16 bg-gradient-to-r from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Year-wise Success Stories
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Track record of our students' achievements over the years
          </p>
        </div>
        
        {/* Year selector tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-md shadow-sm">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => setActiveYear(year)}
                className={`px-6 py-3 text-base font-medium ${
                  activeYear === year
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${
                  years.indexOf(year) === 0
                    ? 'rounded-l-md'
                    : years.indexOf(year) === years.length - 1
                    ? 'rounded-r-md'
                    : ''
                } border border-gray-300 focus:z-10 focus:outline-none transition duration-300`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
        
        {/* Statistics cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="text-3xl font-bold text-blue-600">{currentYearData.totalStudents}</div>
            <div className="text-sm text-gray-600">Total Students</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="text-3xl font-bold text-blue-600">{currentYearData.medicalAdmissions}</div>
            <div className="text-sm text-gray-600">O level</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="text-3xl font-bold text-blue-600">{currentYearData.engineeringAdmissions}</div>
            <div className="text-sm text-gray-600">A level</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="text-3xl font-bold text-blue-600">{currentYearData.scienceDegrees}</div>
            <div className="text-sm text-gray-600">A*</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="text-3xl font-bold text-blue-600">{currentYearData.topScorers}</div>
            <div className="text-sm text-gray-600">Top Scorers</div>
          </div>
        </div>
        
        {/* Highlights and featured students */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Highlights */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Highlights of {activeYear}</h3>
            <ul className="space-y-3">
              {currentYearData.highlights.map((highlight: string, index: number) => (
                <li key={index} className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Featured students */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Featured Students</h3>
            <div className="space-y-4">
              {currentYearData.featuredStudents.map((student: StudentType, index: number) => (
                <div key={index} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">{student.name.charAt(0)}</span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-base font-semibold text-gray-900">{student.name}</h4>
                      <p className="text-sm text-blue-600">{student.achievement}</p>
                      <p className="text-xs text-gray-500">{student.institution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-10 text-center">
          <a 
            href="#testimonials" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            Read student testimonials
            <svg className="ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Success;
